import { Actions, ofType, Effect } from '@ngrx/effects';
import {
  FormsActionTypes,
  RemoveFormArrayItem,
  AddFormArrayItem,
  SubmitSuccessful,
  SetDocumentId,
  OpenCreateDialog,
  SubmitValid,
  SubmitInvalid,
  SetFormData,
  InitForm,
  DestroyForm,
  SubmitError,
  OpenCreateRoute,
  OpenEditRoute,
  OpenEditDialog,
  OpenEditFormInWindow,
  OpenFormInWindow,
  OpenCreateFormInWindow,
  CancelForm,
  ResetForm,
  DxSetValueAction,
} from './forms.actions';
import {
  concatMap,
  map,
  first,
  switchMap,
  mergeMap,
  filter,
  tap,
  debounceTime,
  startWith,
  distinctUntilChanged,
  delay,
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import {
  SetValueAction,
  RemoveArrayControlAction,
  MarkAsSubmittedAction,
  ResetAction,
  MarkAsUnsubmittedAction,
  unbox,
  MarkAsDirtyAction,
  MarkAsTouchedAction,
  SetAsyncErrorAction,
  ClearAsyncErrorAction,
  StartAsyncValidationAction,
  SetUserDefinedPropertyAction,
  DisableAction,
  EnableAction
} from 'ngrx-forms';
import { Injectable } from '@angular/core';
import {
  getForm,
  getFormsWithUnqieFields,
  getFormValue,
  UnquieFieldItem
} from './forms.selectors';
import { DigiAIXCoreAppState } from '../app-state';
import {
  CreateOrUpdateDocument,
  DocumentCreated,
  DocumentActionTypes,
  DocumentUpdated,
  GetDocument,
  CreateFailed,
  UpdateFailed,
  DocumentAdded
} from '../documents/document.actions';
import { getDocumentById } from '../documents/document.selectors';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material';
import {
  ShowInfoMessage,
  ShowErrorMessage,
  ShowWarningMessage
} from '../notification/notification.actions';
import { all } from 'deepmerge';
import { combineLatest } from 'rxjs';
import { FormsService } from './forms.service';
import { getValueByObjectPath } from './helpers';
import { DxFormState } from './form-state';
import { FormsSources } from './forms-sources';
import { Router } from '@angular/router';
import { FORM_CONTROL_DISABLED_KEY } from './form-control';
import { v1 as uuid } from 'uuid';

@Injectable()
export class FormsEffects<AppState extends DigiAIXCoreAppState, Form, RawForm> {
  @Effect()
  public openEditFormInWindow$ = this.actions$.pipe(
    ofType<OpenEditFormInWindow>(OpenEditFormInWindow.TYPE),
    switchMap(action => [
      new SetDocumentId(action.formId, action.documentId),
      new OpenFormInWindow(
        action.formId,
        action.tracer,
        (action.stack || []).concat(['FormsEffects::openEditFormInWindow$'])
      )
    ])
  );

  @Effect()
  public openCreateFormInWindow$ = this.actions$.pipe(
    ofType<OpenCreateFormInWindow<any>>(OpenCreateFormInWindow.TYPE),
    map(
      action =>
        new OpenFormInWindow(
          action.formId,
          action.tracer,
          (action.stack || []).concat(['FormsEffects::openCreateFormInWindow$'])
        )
    )
  );

  @Effect()
  public setDisableUserDefinedProperty$ = this.actions$.pipe(
    ofType<SetUserDefinedPropertyAction>(SetUserDefinedPropertyAction.TYPE),
    filter(action => action.name === FORM_CONTROL_DISABLED_KEY),
    map(action =>
      action.value
        ? new DisableAction(action.controlId)
        : new EnableAction(action.controlId)
    )
  );

  @Effect({ resubscribeOnError: true })
  public submitForm$ = this.actions$.pipe(
    ofType<MarkAsSubmittedAction>(MarkAsSubmittedAction.TYPE),
    mergeMap(action =>
      this.store$.pipe(
        select(getForm<Form, RawForm>(action.controlId)),
        first(),
        filter(formState => !!formState),
        filter(formState => !formState.withDefinition),
        filter(formState => formState.form.isValid),
        tap(() =>
          this.logger.info(
            `[FormsEffects::submitValidForm$] Form >${
              action.controlId
            }< is valid`
          )
        ),
        map(
          formState =>
            new SubmitValid(
              action.controlId,
              unbox(formState.form.value),
              formState.collectionPath ||
                formState.form.userDefinedProperties.collectionPath
            )
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public submitValidForm$ = this.actions$.pipe(
    ofType<SubmitValid>(FormsActionTypes.SubmitValid),
    filter(action => !!action.collectionPath),
    switchMap(action =>
      this.store$.select(getForm(action.formId)).pipe(
        first(),
        switchMap(state => {
          if (state.onlyLocal) {
            const id = `${action.collectionPath}/LOCAL_${uuid()}`;
            return [
              new DocumentAdded(
                action.collectionPath,
                { ...action.value, id: id },
                `form_${action.formId}`
              ),
              new SubmitSuccessful(
                action.formId,
                { ...action.value, id: id },
                `form_${action.formId}`
              )
            ];
          } else {
            return [
              new CreateOrUpdateDocument(
                action.collectionPath,
                action.value,
                `form_${action.formId}`
              )
            ];
          }
        })
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public addInitOnOpenCreateDialogOrRoute$ = this.actions$.pipe(
    ofType<OpenCreateDialog | OpenCreateRoute | OpenCreateFormInWindow<any>>(
      FormsActionTypes.OpenCreateDialog,
      OpenCreateRoute.TYPE,
      OpenCreateFormInWindow.TYPE
    ),
    filter(action => !!action.initial),
    switchMap(action =>
      this.store$.pipe(
        select(getForm(action.formId)),
        first(),
        filter(formState => !!formState),
        filter(formState => !formState.form || !formState.form.value || !formState.form.value['id']),
        map(formState => ({ ...formState.initial, ...action.initial })),
        map(initial => new DxSetValueAction(action.formId, initial, 'FormsEffects::addInitOnOpenCreateDialogOrRoute'))
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public openEditDialog$ = this.actions$.pipe(
    ofType<OpenEditDialog>(FormsActionTypes.OpenEditDialog),
    switchMap(action => [
      new SetDocumentId(action.formId, action.documentId),
      new OpenCreateDialog(action.formId)
    ])
  );

  @Effect({ resubscribeOnError: true })
  public createSubmitSuccessful$ = this.actions$.pipe(
    ofType<DocumentCreated<any>>(DocumentActionTypes.Created),
    filter(action => !!action.tracer),
    filter(action => !!action.tracer.match(/^form_/)),
    switchMap(action => [
      new ShowInfoMessage('Das Anlegen war erfolgreich!'),
      new SubmitSuccessful<any>(
        action.tracer.replace(/^form_/, ''),
        action.document,
        action.tracer
      )
    ])
  );

  @Effect()
  public resetForm$ = this.actions$.pipe(
    ofType<ResetForm>(ResetForm.TYPE),
    map(action => new ResetAction(action.formId))
  );

  @Effect({ resubscribeOnError: true })
  public updateSubmitSuccessful$ = this.actions$.pipe(
    ofType<DocumentUpdated<any>>(DocumentActionTypes.Updated),
    filter(action => !!action.tracer),
    filter(action => !!action.tracer.match(/^form_/)),
    switchMap(action => [
      new ShowInfoMessage('Die Aktualisierung war erfolgreich!'),
      new SubmitSuccessful(
        action.tracer.replace(/^form_/, ''),
        action.document,
        action.tracer
      )
    ])
  );

  @Effect({ resubscribeOnError: true })
  public createSubmitFailed$ = this.actions$.pipe(
    ofType<CreateFailed<any>>(CreateFailed.TYPE),
    filter(action => !!action.tracer),
    filter(action => !!action.tracer.match(/^form_/)),
    switchMap(action => [
      new ShowWarningMessage('Das Anlegen war nicht erfolgreich!'),
      new SubmitError(action.tracer.replace(/^form_/, ''), action.error)
    ])
  );

  @Effect({ resubscribeOnError: true })
  public updateSubmitFailed$ = this.actions$.pipe(
    ofType<UpdateFailed<any>>(UpdateFailed.TYPE),
    filter(action => !!action.tracer),
    filter(action => !!action.tracer.match(/^form_/)),
    switchMap(action => [
      new ShowWarningMessage('Die Aktualisierung war nicht erfolgreich!'),
      new SubmitError(action.tracer.replace(/^form_/, ''), action.error)
    ])
  );

  @Effect({ resubscribeOnError: true })
  public submitError$ = this.actions$.pipe(
    ofType<SubmitError>(FormsActionTypes.SubmitError),
    map(action => new MarkAsUnsubmittedAction(action.formId))
  );

  @Effect({ resubscribeOnError: true })
  public submitSuccessful$ = this.actions$.pipe(
    ofType<SubmitSuccessful<any>>(FormsActionTypes.SubmitSuccessful),
    map(action => new ResetForm(action.formId, action.tracer))
  );

  @Effect({ resubscribeOnError: true })
  public submitInvalidForm$ = this.actions$.pipe(
    ofType<MarkAsSubmittedAction>(MarkAsSubmittedAction.TYPE),
    mergeMap(action =>
      this.store$.pipe(
        select(getForm<Form, RawForm>(action.controlId)),
        first(),
        filter(formState => !!formState),
        filter(formState => formState.form.isInvalid),
        tap(formState =>
          this.logger.warn(
            `[FormsEffects::submitInvalidForm$] Form >${
              action.controlId
            }< is not valid`,
            formState.form.errors,
            unbox(formState.form.value)
          )
        ),
        switchMap(formState => [
          new SubmitInvalid(
            action.controlId,
            unbox(formState.form.value),
            formState.form.errors
          ),
          new MarkAsUnsubmittedAction(action.controlId),
          new MarkAsDirtyAction(action.controlId),
          new MarkAsTouchedAction(action.controlId),
          new ShowErrorMessage('Die Eingaben in des Formular sind Fehlerhaft!')
        ])
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentAndLoad$ = this.actions$.pipe(
    ofType<SetDocumentId>(FormsActionTypes.SetDocumentId),
    filter(action => !!action.documentId),
    mergeMap(action =>
      this.store$.pipe(
        select(getForm<Form, RawForm>(action.formId)),
        first(),
        filter(formState => !!formState && !!formState.collectionPath),
        map(
          formState =>
            new GetDocument(
              formState.form.userDefinedProperties.collectionPath ||
                formState.collectionPath,
              action.documentId,
              '[FormsEffects::setDocumentAndLoad$]'
            )
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentId$ = this.actions$.pipe(
    ofType<SetDocumentId>(FormsActionTypes.SetDocumentId),
    filter(action => !!action.documentId),
    mergeMap(action =>
      this.store$.pipe(
        select(getForm<Form, RawForm>(action.formId)),
        first(),
        filter(formState => !!formState && !!formState.collectionPath),
        switchMap(formState =>
          this.store$.pipe(
            select(
              getDocumentById(formState.collectionPath, action.documentId)
            ),
            filter(document => !!document),
            first(),
            delay(100),
            map(
              document =>
                new DxSetValueAction(
                  action.formId,
                  formState.box(all([formState.initial, document]) as any),
                  'FormsEffects::setDocumentId'
                )
            )
          )
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public removeFormArrayItem$ = this.actions$.pipe(
    ofType<RemoveFormArrayItem>(FormsActionTypes.RemoveFormArrayItem),
    map(
      action =>
        new RemoveArrayControlAction(
          [action.formId, action.path].join('.'),
          action.index
        )
    )
  );

  @Effect()
  public cancelForm$ = this.actions$.pipe(
    ofType<CancelForm>(CancelForm.TYPE),
    switchMap(action =>
      this.store$.select(getFormValue(action.formId)).pipe(
        first(),
        filter(Boolean),
        filter(value => !!value.id),
        map(() => new ResetForm(action.formId, action.tracer))
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public addFormArrayItem$ = this.actions$.pipe(
    ofType<AddFormArrayItem>(FormsActionTypes.AddFormArrayItem),
    concatMap(action =>
      this.store$.pipe(
        select(getForm<Form, RawForm>(action.formId)),
        first(),
        map(formState => {
          const formArrayValue: ReadonlyArray<{
            order: number;
          }> | null = getValueByObjectPath(formState.form.value, action.path);

          if (!formArrayValue || !Array.isArray(formArrayValue)) {
            console.error({ formState, action, formArrayValue });
            throw new Error('Form array value not found');
          }

          const order = formArrayValue.length
            ? [...formArrayValue].sort((a, b) => b.order - a.order)[0].order * 2
            : 1;

          return new DxSetValueAction([action.formId, action.path].join('.'), [
            ...formArrayValue,
            {
              ...getValueByObjectPath(formState.arrayInitial, action.path),
              order
            }
          ], 'FormsEffects::addFormArrayItem');
        })
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public setFormData$ = this.actions$.pipe(
    ofType<SetFormData>(FormsActionTypes.SetFormData),
    switchMap(action =>
      this.store$.pipe(
        select(getForm(action.formId)),
        first(),
        filter(Boolean),
        map(
          (formState: DxFormState<any, any>) =>
            new DxSetValueAction(action.formId, formState.box(action.data), 'FormsEffects::setFormData')
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public uniqueValidate$ = combineLatest(
    this.store$.pipe(
      select(getFormsWithUnqieFields),
      distinctUntilChanged((a, b) => a.length === b.length)
    ),
    this.actions$.pipe(
      ofType<SetValueAction<string | number>>(SetValueAction.TYPE)
    )
  ).pipe(
    map(([forms, action]) => [
      forms.find(form => form.controlId === action.controlId),
      action.value
    ]),
    filter(([form]) => !!form),
    distinctUntilChanged((a, b) => a[1] === b[1]),
    debounceTime(500),
    switchMap(([form, value]: [any, string]) =>
      this.store$.pipe(
        select(getFormValue<any>(form.formId)),
        first(),
        map(formValue => formValue.id),
        map(id => [form, value, id])
      )
    ),
    switchMap(([form, value, id]: [UnquieFieldItem, string, string]) =>
      this.formsService
        .isUnique(form.collectionPath, form.path, value, !!id)
        .pipe(
          map(isUnique => {
            if (!isUnique) {
              return new SetAsyncErrorAction(
                form.controlId,
                'unique',
                'Die Eingabe muss global eindeutig sein'
              );
            } else {
              return new ClearAsyncErrorAction(form.controlId, 'unique');
            }
          }),
          startWith(new StartAsyncValidationAction(form.controlId, 'unique'))
        )
    )
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public initForm$ = this.actions$.pipe(
    ofType<InitForm>(InitForm.TYPE),
    tap(action => {
      const fd = this.formsSources.getForm(action.formId);

      if (!fd) {
        this.logger.error(
          `[FormsEffects::initForm$::${
            action.formId
          }] Form Definition not found`
        );
      } else {
        this.logger.info(
          `[FormsEffects::initForm$::${action.formId}] Form Definition init`
        );
        fd.ngOnInit();
      }
    })
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public destroyForm$ = this.actions$.pipe(
    ofType<DestroyForm>(DestroyForm.TYPE),
    tap(action => {
      const fd = this.formsSources.getForm(action.formId);

      if (!fd) {
        this.logger.error(
          `[FormsEffects::initForm$::${
            action.formId
          }] Form Definition not found`
        );
      } else {
        this.logger.info(
          `[FormsEffects::initForm$::${
            action.formId
          }] Form Definition destroyed`
        );
        fd.ngOnDestroy();
      }
    })
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public openCreateRoute$ = this.actions$.pipe(
    ofType<OpenCreateRoute>(OpenCreateRoute.TYPE),
    tap(action => this.router.navigate(['/', 'form', action.formId]))
  );

  @Effect({ resubscribeOnError: true })
  public openEditRoute$ = this.actions$.pipe(
    ofType<OpenEditRoute>(OpenEditRoute.TYPE),
    tap(action => this.router.navigate(['/', 'form', action.formId])),
    map(action => new SetDocumentId(action.formId, action.documentId))
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly logger: NGXLogger,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly formsService: FormsService,
    private readonly formsSources: FormsSources
  ) {}
}
